import {availableBackground,drawBackground} from './Backgrounds';
import {createBackgroundCompositor} from './backgroundCompositor';
import { DEFAULT_FILTER, FILTERS, getFaceBBox } from './FaceFiltersRegistry';
import { loadFaceDetector } from './faceDetector';

// Both the local preview and WebRTC use this composited stream. Detection and
// rendering use the SAME frozen frame so effects cannot lag behind a newer face.
export function createProtectedStream(source, isRevealed, getFilter = () => DEFAULT_FILTER, onStatus = () => {}, {getBackground=()=>'none',onBackgroundStatus=()=>{},previewWidth=640,frameRate=15} = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = previewWidth;
  canvas.height = Math.round(previewWidth * 3 / 4);
  if (!canvas.captureStream) throw new Error("פילטרים חיים אינם זמינים בדפדפן הזה.");
  const context = canvas.getContext('2d');
  const snapshot = document.createElement('canvas');
  const composite = document.createElement('canvas');
  snapshot.width = composite.width = canvas.width;
  snapshot.height = composite.height = canvas.height;
  const snapshotContext = snapshot.getContext('2d');
  const compositeContext = composite.getContext('2d');
  if (!context || !snapshotContext || !compositeContext) throw new Error("לא ניתן להפעיל פילטרים חיים.");
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = source;
  const backgrounds=createBackgroundCompositor(onBackgroundStatus);
  let playbackFailed = false;
  const startPlayback = async () => {
    try { await video.play(); playbackFailed = false; }
    catch { playbackFailed = true; }
  };
  let stopped = false;
  let frame;
  let detector;
  let loading = false;
  let loadFailed = false;
  let detecting = false;
  const timers = new Map();
  // Bound waits without starting overlapping inference on the shared snapshot.
  const withTimeout = (promise, milliseconds) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      reject(new Error('עיבוד הווידאו נמשך זמן רב מדי'));
    }, milliseconds);
    timers.set(timer, reject);
    Promise.resolve(promise).then(resolve, reject).finally(() => {
      clearTimeout(timer);
      timers.delete(timer);
    });
  });
  let lastStatus;
  const status = value => {
    if (value !== lastStatus && !stopped) { lastStatus = value; onStatus(value); }
  };
  const fallback = (state, message) => {
    context.fillStyle = '#10172b';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.textAlign = 'center';
    context.font = `${90 * canvas.width / 640}px sans-serif`;
    context.fillText(FILTERS[getFilter()]?.icon || '🎭', canvas.width / 2, canvas.height * 215 / 480);
    context.fillStyle = '#e2e8f0';
    context.font = `${22 * canvas.width / 640}px sans-serif`;
    context.fillText(message, canvas.width / 2, canvas.height * 300 / 480);
    status(state);
  };
  const drawCamera = target => {
    // Keep portrait and widescreen cameras in proportion, including landmarks.
    const width = video.videoWidth || canvas.width;
    const height = video.videoHeight || canvas.height;
    const scale = Math.min(canvas.width / width, canvas.height / height);
    target.fillStyle = '#10172b';
    target.fillRect(0, 0, canvas.width, canvas.height);
    target.drawImage(video, (canvas.width - width * scale) / 2, (canvas.height - height * scale) / 2, width * scale, height * scale);
  };
  const retry = async () => {
    if (loading || stopped) return;
    startPlayback();
    loading = true;
    const reload = loadFailed;
    loadFailed = false;
    try {
      const loaded = await withTimeout(loadFaceDetector(reload), 15000);
      if (!stopped) detector = loaded;
    } catch (error) {
      if (!stopped) loadFailed = true;
    } finally { loading = false; }
  };
  const paint = async () => {
    if (stopped) return;
    try {
      const camera = source.getVideoTracks?.()[0];
      if (camera?.readyState === 'ended') fallback('camera-ended', "המצלמה התנתקה • השיחה הקולית ממשיכה");
      else if (playbackFailed) fallback('playback', "תצוגת המצלמה מושהית");
      else if (camera?.muted || camera?.enabled === false || video.readyState < 2) fallback('camera', "ממתינים למצלמה…");
      else if (isRevealed()) {
        if(getBackground()==='none')drawCamera(context);
        else {
          drawCamera(snapshotContext);
          if(composite.width!==canvas.width||composite.height!==canvas.height){composite.width=canvas.width;composite.height=canvas.height;}
          await backgrounds.apply(snapshot,compositeContext,getBackground());
          if(stopped||!isRevealed())return;
          context.drawImage(composite,0,0);
        }
        status('revealed');
      } else if (!detector) {
        fallback(loadFailed ? 'error' : 'loading', loadFailed ? "לא ניתן לטעון פילטרים לפנים" : "טוענים את הפילטר שלכם…");
      } else if (detecting) {
        fallback('slow', "זיהוי הפנים מתעכב • השיחה הקולית ממשיכה");
      } else {
        drawCamera(snapshotContext);
        detecting = true;
        let faces;
        try {
          const detection = Promise.resolve().then(() => detector(snapshot)).finally(() => { detecting = false; });
          faces = await withTimeout(detection, 2000);
        } catch (error) {
          if (!stopped) fallback(detecting ? 'slow' : 'error', "זיהוי הפנים נקטע • השיחה הקולית ממשיכה");
          return;
        }
        if (stopped) return;
        // Consent may change while inference is running. Discard this old frame.
        if (isRevealed()) return;
        if (camera?.readyState === 'ended' || camera?.muted || camera?.enabled === false) {
          fallback('camera', "ממתינים למצלמה…");
          return;
        }
        const filter = FILTERS[getFilter()];
        const boxes = faces.map(face => getFaceBBox(face.landmarks, snapshot));
        if (!faces.length) {
          // Keep the camera live, but obscure the entire frame until tracking returns.
          // Downsample first, then enlarge without smoothing; never publish the raw snapshot.
          if(getBackground()!=='none'){drawBackground(context,availableBackground(getBackground()),canvas.width,canvas.height,performance.now());status('no-face');return;}
          composite.width = 20;
          composite.height = 15;
          compositeContext.drawImage(snapshot, 0, 0, composite.width, composite.height);
          context.save();
          try {
            context.imageSmoothingEnabled = false;
            context.drawImage(composite, 0, 0, canvas.width, canvas.height);
          } finally { context.restore(); }
          status('no-face');
        }
        else if (!filter || boxes.some(box => !box || !Number.isFinite(box.width) || box.width <= 0 || box.height <= 0)) {
          fallback('error', "הפילטר אינו זמין");
        } else {
          // Compose offscreen. Never publish an intermediate, unfiltered frame.
          if (composite.width !== canvas.width || composite.height !== canvas.height) {
            composite.width = canvas.width;
            composite.height = canvas.height;
          }
          await backgrounds.apply(snapshot,compositeContext,getBackground());
          if(stopped||isRevealed())return;
          faces.forEach((face, index) => {
            compositeContext.save();
            try { filter.apply(compositeContext, snapshot, face.landmarks, boxes[index], ...(getBackground()==='none'?[]:[{backgroundActive:true}])); }
            finally { compositeContext.restore(); }
          });
          context.drawImage(composite, 0, 0);
          status('tracking');
        }
      }
    } catch (error) {
      if (!stopped) fallback('error', "הפילטר נקטע");
    } finally {
      if (!stopped) frame = requestAnimationFrame(paint);
    }
  };
  fallback('loading', "טוענים את הפילטר שלכם…");
  const output = canvas.captureStream(frameRate);
  source.getAudioTracks().forEach(track => output.addTrack(track));
  const ready = retry();
  frame = requestAnimationFrame(paint);
  return {
    stream: output,
    ready,
    retry,
    stop({ keepAudio = false } = {}) {
      if (stopped) return;
      stopped = true;
      backgrounds.stop();
      cancelAnimationFrame(frame);
      timers.forEach((reject, timer) => {
        clearTimeout(timer);
        reject(new Error("הווידאו נעצר"));
      });
      timers.clear();
      new Set([...output.getTracks(), ...source.getTracks()]).forEach(track => {
        if (!keepAudio || track.kind !== 'audio') track.stop();
      });
      video.pause();
      video.srcObject = null;
    },
  };
}
